import { useEffect, useState } from "react";
import {
  Form,
  Input,
  Modal,
  Row,
  Col,
  Tag,
  Typography,
  Select,
  Divider,
  Button,
  InputNumber,
  Empty,
  Spin,
  Space,
} from "antd";
import { debounce } from "lodash";
import MainContainer from "../../containers/MainContainer";
import SignedURLImage from "../../components/SignedURLImage";
import EquipmentModal from "../../components/EquipmentModal";
import TrayFormModal from "../../components/TrayFormModal";
import Uploader from "../../components/Uploader";
import toastNotification from "../../components/toastNotification";
import trayService from "../../services/trayService";
import manufacturerService from "../../services/manufacturerService";
import equipmentService from "../../services/equipmentService";
import mediaService from "../../services/mediaService";
import { ReactComponent as TagCloseIcon } from "../../assets/icons/iconTagClose.svg";
import { ReactComponent as DeleteIcon } from "../../assets/icons/iconDelete.svg";
import { ReactComponent as CheckboxSelectedIcon } from "../../assets/icons/checkboxSelected.svg";
import equipmentPlaceholder from "../../assets/equipment-placeholder.svg";
import trayPlaceholder from "../../assets/tray-placeholder.svg";

const { confirm } = Modal;
const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const ManufacturerTrayFrom = (props) => {
  const {
    match: { params },
    history,
    location: { state },
  } = props;
  const customPageSize = 30;

  const trayId = params.trayid ? params.trayid : null;
  const manufacturerId = params.id ? params.id : null;

  //TODO :: Check if invalid manufacturer id
  const pathToManufacturerTrays = `/manufacturer/${manufacturerId}/tray`;

  const [tray, setTray] = useState(state?.record);
  const [loading, setLoading] = useState(false);
  const [equipmentsList, setEquipmentsList] = useState([]);
  const [selectedEquipmentsId, setSelectedEquipmentsId] = useState([]);
  const [selectedEquipments, setSelectedEquipments] = useState([]);
  const [editMode, setEditMode] = useState(
    state?.editMode === true || !trayId ? true : false
  );
  const [imageName, setImageName] = useState();
  const [imageLoading, setImageLoading] = useState(false);
  const [imagesToBeDeleted, setImagesToBeDeleted] = useState([]);
  const [imageRequiredStatus, setImageRequiredStatus] = useState({
    validateStatus: "success",
    errorMsg: null,
  });
  const [equipment, setEquipment] = useState();
  const [showEquipmentModal, setEquipmentModal] = useState(false);
  const [showTrayModal, setShowTrayModal] = useState(false);

  const [timeOut, setTimeOut] = useState(0);
  const [currentEquipmentPage, setCurrentEquipmentPage] = useState(1);
  const [equipmentCount, setEquipmentCount] = useState();
  const [equipmentSearchText, setEquipmentSearchText] = useState();
  const [loadEquipments, setLoadEquipments] = useState(false);

  const [manufacturer, setManufacturer] = useState();

  const [form] = Form.useForm();

  const overlayMediaDelete = (
    <div className="icon-overlay">
      <Button
        icon={<DeleteIcon />}
        onClick={() => deleteTrayImage()}
        style={{ padding: 0 }}
      />
    </div>
  );

  const closeEquipmentModal = () => {
    const video = document.getElementById("videoEquipment");
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
    setEquipmentModal(false);
  };

  const customImageUpload = async (file) => {
    setImageLoading(true);
    let fileName;
    if (file.size > 30000000) {
      fileName = await mediaService.uploadMultipartImage(file);
    } else {
      fileName = await mediaService.uploadImageUsingPresignedUrl(file);
    }
    if (fileName) {
      setImageName(fileName);
      setImageRequiredStatus({
        validateStatus: "success",
        errorMsg: null,
      });
    } else {
      setImageName(null);
      setImageLoading(false);
      setImageRequiredStatus({
        validateStatus: "error",
        errorMsg: "Upload failed",
      });
    }
  };

  const onEquipmentSelect = (equipmentId) => {
    // add id to selectedIds to maintain state of selected options
    const ids = selectedEquipmentsId;
    ids.push(equipmentId);
    setSelectedEquipmentsId([...ids]);

    // add equipment to selectedEquipments to add it to the displayed tag list
    const selectedEquipment = {
      equipment: equipmentsList.find(
        (equipment) => equipment.id === equipmentId
      ),
      quantity: 1,
    };
    selectedEquipments.push(selectedEquipment);
    setSelectedEquipments([...selectedEquipments]);
  };

  const onEquipmentDeselect = (equipmentId) => {
    // remove id from selectedIds to maintain state of selected options
    setSelectedEquipmentsId(
      selectedEquipmentsId.filter((id) => id !== equipmentId)
    );

    // remove equipment from selectedEquipments to remove it from the displayed tag list
    setSelectedEquipments(
      selectedEquipments.filter(
        (trayEquipment) => trayEquipment.equipment.id !== equipmentId
      )
    );
  };

  const showDeleteConfirmation = () => {
    confirm({
      title: "Delete Tray",
      content: "Are you sure you want to delete this tray?",
      okText: "Delete",
      cancelText: "Cancel",
      centered: true,
      async onOk() {
        setLoading(true);
        const isDeleted = await trayService.patchTray({
          id: trayId,
          isDeleted: true,
        });

        if (isDeleted) {
          toastNotification("success", "Tray Deleted Successfully");
          history.push(pathToManufacturerTrays);
        } else {
          toastNotification("error", "Error deleting the tray");
        }
        setLoading(false);
      },
    });
  };

  const onFinish = async (trayFormValues) => {
    setLoading(true);

    //Validate if tray image is missing
    if (imageName) {
      trayFormValues.image = imageName;
    } else trayFormValues.image = "";

    trayFormValues.distributor = manufacturerId;
    trayFormValues.equipments = selectedEquipments.map((trayEquipment) => ({
      equipment: trayEquipment.equipment.id,
      quantity: trayEquipment.quantity,
    }));

    //delete previously uploaded images if any
    for (const img of imagesToBeDeleted) {
      await mediaService.deleteMedia(img);
    }
    setImagesToBeDeleted([]);

    if (trayId) {
      trayFormValues.id = trayId;
      await editTray(trayFormValues, true);
    } else {
      await addTray(trayFormValues);
    }
    setLoading(false);
  };

  const pageHeaderProps = trayId
    ? {
        title: tray.trayName ? tray.trayName : "Tray Details",
        btnText: editMode ? "Save" : "Edit",
        btnType: editMode ? "primary" : "secondary",
        topActionMenuItems: [
          {
            title: "Delete Tray",
            path: "",
            onClick: showDeleteConfirmation,
          },
        ],
      }
    : {
        title: "New Tray",
        btnText: "Add",
      };

  /** API */

  const addTray = async (trayFormValues) => {
    // TODO :: Trim values before submit
    const res = await trayService.postTray(trayFormValues);
    if (res.status === 200) {
      toastNotification("success", "New tray added");
      history.push(pathToManufacturerTrays);
    } else {
      toastNotification("error", "Error adding a new Tray");
    }
  };

  const editTray = async (trayFormValues, needRedirect) => {
    const res = await trayService.patchTray(trayFormValues);
    if (res.status === 200) {
      toastNotification("success", "Tray Updated");
      setTray(res.data);
      setEditMode(false);
    } else {
      toastNotification("error", "Error updating the Tray");
    }
    setLoading(false);
  };

  const fetchTray = async () => {
    const data = await trayService.getTray(manufacturerId, trayId);
    if (data) {
      setTray(data);
      return data;
    } else {
      toastNotification("error", "Requested Tray record not found");
      history.push(pathToManufacturerTrays);
    }
  };

  const deleteTrayImage = async () => {
    setImageName(null);
    setImagesToBeDeleted((deleteArray) => {
      deleteArray.push(imageName);
      return [...deleteArray];
    });

    setImageLoading(false);
    return true;
  };
  /** API End */

  const logValue = debounce((searchText) => {
    setEquipmentSearchText(searchText);
  }, 250);

  const addOptions = async () => {
    setLoadEquipments(true);
    setCurrentEquipmentPage(currentEquipmentPage + 1);
    await populateEquipmentsList(
      customPageSize,
      currentEquipmentPage * customPageSize,
      equipmentSearchText
    );
    setLoadEquipments(false);
  };

  const handleOpen = () => {
    setCurrentEquipmentPage(1);
  };

  const handleChange = (value) => {
    setCurrentEquipmentPage(0);
  };

  const handleInputChange = (value) => {
    logValue(value);
    if (value.length === 0) setCurrentEquipmentPage(1);
  };

  const handleMenuScrollToBottom = () => {
    loadEquipments && equipmentsList.length !== equipmentCount && addOptions();
  };

  const getEquipmentCount = async (searchText) => {
    const count = await equipmentService.getEquipmentCount(
      manufacturerId,
      false,
      searchText
    );
    if (count >= 0) {
      setEquipmentCount(count);
      return true;
    } else {
      return false;
    }
  };

  const onSearchFunction = async (searchValue) => {
    if (timeOut) {
      clearTimeout(timeOut);
    }
    setTimeOut(
      setTimeout(async () => {
        searchValue = searchValue.trim();
        setEquipmentSearchText(searchValue);
        await getEquipmentCount(searchValue);
        setCurrentEquipmentPage(0);
        populateEquipmentsList(customPageSize, 0, searchValue);
      }, 500)
    );
  };

  const handleFilterOption = (input, option) => {
    return option.value !== "loading"
      ? option.title.toLowerCase().indexOf(input.toLowerCase()) >= 0
      : false;
  };

  const populateEquipmentsList = async (limit, skip, searchValue) => {
    setLoadEquipments(true);
    const res = await manufacturerService.getManufacturerEquipments(
      manufacturerId,
      false,
      limit,
      skip,
      searchValue
    );
    if (res) {
      setLoadEquipments(res.length === customPageSize);
      const arr = [...equipmentsList, ...res];
      setEquipmentsList([
        ...new Map(arr.map((item) => [item["id"], item])).values(),
      ]);
    }
    setLoadEquipments(false);
  };

  useEffect(() => {
    setLoading(true);
    (async () => {
      if (trayId) {
        // If location.state doesn't contain the tray, get it from api
        if (!tray) await fetchTray();

        if (tray) {
          form.setFieldsValue({ ...tray });
          setImageName(tray.image);

          // Get tray equipments and set the selected equipments
          const trayEquipments = await trayService.getTrayEquipments(trayId);
          if (trayEquipments) {
            const manufacturerDetails =
              await manufacturerService.getManufacturer(manufacturerId);
            manufacturerDetails && setManufacturer(manufacturerDetails);
            setSelectedEquipments(trayEquipments);
            setSelectedEquipmentsId(
              trayEquipments.map((trayEquipment) => trayEquipment.equipment.id)
            );
          }
        }
      }

      // fetch all manufacturer equipments to fill the select equipment dropdown
      const success = await getEquipmentCount();
      if (success) populateEquipmentsList(customPageSize, 0);

      setLoading(false);
    })();
  }, []);

  return (
    <MainContainer
      formName="manufacturerTrayAddEditForm"
      backBtnPath={pathToManufacturerTrays}
      routeState={{ pageNumber: state?.pageNumber }}
      onBtnClick={() => {
        if (!editMode) {
          setEditMode(true);
        } else {
          form.submit();
        }
      }}
      divider={true}
      {...pageHeaderProps}
    >
      <Spin spinning={loading}>
        <div style={{ marginTop: "1rem" }}>
          <Row>
            <Col span={14}>
              <Row align="middle">
                <Col
                  xs={{ span: 24, offset: 0 }}
                  sm={{ span: 24, offset: 0 }}
                  md={{ span: 24, offset: 0 }}
                  lg={{ span: 22, offset: 1 }}
                  xl={{ span: 22, offset: 1 }}
                  xxl={{ span: 22, offset: 1 }}
                >
                  <Form
                    form={form}
                    name="manufacturerTrayAddEditForm"
                    id="manufacturerTrayAddEditForm"
                    size="large"
                    colon="false"
                    scrollToFirstError="true"
                    onFinish={onFinish}
                    layout="vertical"
                    requiredMark={false}
                  >
                    <Row>
                      <Col span={24}>
                        <Title level={5}>Tray Details</Title>
                      </Col>
                    </Row>
                    <Row>
                      <Col span={24}>
                        <Form.Item
                          label="Tray Name (Required)"
                          name="trayName"
                          rules={[
                            {
                              required: true,
                              message: "Please input tray name!",
                            },
                          ]}
                        >
                          <Input disabled={!editMode} />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row>
                      <Col span={24}>
                        <Form.Item label="Tray Description" name="description">
                          <TextArea
                            disabled={!editMode}
                            style={{ resize: "none" }}
                            rows={4}
                            autoSize={false}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row>
                      <Col span={24}>
                        <Form.Item
                          label="Supported formats: jpeg, jpg, png, heic."
                          name="imageItem"
                          disabled={!editMode}
                        >
                          <div className="icon-overlay-container">
                            {imageName ? (
                              <div style={{ height: "12rem", width: "14rem" }}>
                                <SignedURLImage
                                  fileName={imageName}
                                  height="100%"
                                  width="100%"
                                  draggable="false"
                                  fallback={trayPlaceholder}
                                  onClick={() => setShowTrayModal(true)}
                                />
                                {editMode && overlayMediaDelete}
                              </div>
                            ) : (
                              <Uploader
                                id="uploadTray"
                                imageLoading={imageLoading}
                                customRequest={(file) =>
                                  customImageUpload(file)
                                }
                                editMode={!editMode ? true : false}
                              />
                            )}
                          </div>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Form>
                </Col>
              </Row>
            </Col>
            <Col span={1}>
              <Divider type="vertical" style={{ height: "100%" }} />
            </Col>
            <Col span={8}>
              <Title level={5}>Add Instruments</Title>
              {editMode && (
                <Select
                  mode="multiple"
                  size="large"
                  style={{ width: "100%" }}
                  showSearch
                  allowClear
                  autoClearSearchValue={false}
                  onSelect={onEquipmentSelect}
                  onDeselect={onEquipmentDeselect}
                  onDropdownVisibleChange={handleOpen}
                  onChange={handleChange}
                  onSearch={onSearchFunction}
                  onPopupScroll={handleMenuScrollToBottom}
                  onInputKeyDown={handleInputChange}
                  filterOption={handleFilterOption}
                  tagRender={() => null}
                  value={selectedEquipmentsId}
                  menuItemSelectedIcon={<CheckboxSelectedIcon />}
                  notFoundContent={
                    <div
                      className="display-flex-center"
                      style={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={<b>No Records Found.</b>}
                      />
                    </div>
                  }
                >
                  {equipmentsList.map((equipment, index) => (
                    <Option key={equipment.id} title={equipment.equipmentName}>
                      <div className="display-flex-center">
                        <SignedURLImage
                          className="fit-image"
                          fileName={equipment.image[0]}
                          height="40px"
                          width="55px"
                          fallback={equipmentPlaceholder}
                          isTrayFormInstruments={true}
                        />
                        <Space
                          direction="vertical"
                          style={{ marginLeft: "10px" }}
                          size={2}
                        >
                          <Text ellipsis strong>
                            {equipment.equipmentName}
                          </Text>
                          <Text
                            ellipsis
                            style={{ color: "grey", fontSize: "12px" }}
                          >
                            {equipment.distributor?.distributorName}
                          </Text>
                        </Space>
                      </div>
                    </Option>
                  ))}
                </Select>
              )}
              <div style={{ marginTop: "1.5rem" }}>
                <Row gutter={[0, 8]}>
                  {selectedEquipments.map((trayEquipment, index) => {
                    return (
                      <Col span={24}>
                        <Tag
                          color="#f5f5f5"
                          key={trayEquipment.id}
                          icon={
                            <SignedURLImage
                              onClick={() => {
                                setEquipment(trayEquipment.equipment);
                                setEquipmentModal(true);
                              }}
                              fallback={equipmentPlaceholder}
                              fileName={trayEquipment.equipment.image[0]}
                              height="50px"
                              width="75px"
                              className="fit-image"
                              isTrayFormInstruments={true}
                            />
                          }
                          closable={editMode}
                          closeIcon={<TagCloseIcon />}
                          onClose={() =>
                            onEquipmentDeselect(trayEquipment.equipment.id)
                          }
                          style={{ color: "black", height: "100%" }}
                        >
                          <Row>
                            <Col span={18} className="display-flex-center">
                              <Space
                                direction="vertical"
                                size={2}
                                style={{ marginLeft: "0.5rem" }}
                              >
                                <Text
                                  ellipsis
                                  strong
                                  style={{ fontSize: "14px", width: "14em" }}
                                >
                                  {trayEquipment.equipment.equipmentName}
                                </Text>
                                <Text
                                  ellipsis
                                  style={{
                                    color: "grey",
                                    fontSize: "12px",
                                    width: "14em",
                                  }}
                                >
                                  {manufacturer?.distributorName}
                                </Text>
                              </Space>
                            </Col>
                            <Col span={6}>
                              <InputNumber
                                disabled={!editMode}
                                style={{ width: "5em" }}
                                defaultValue={trayEquipment.quantity}
                                min={1}
                                max={100}
                                onChange={(value) => {
                                  trayEquipment.quantity = value;
                                }}
                              />
                            </Col>
                          </Row>
                        </Tag>
                      </Col>
                    );
                  })}
                </Row>
              </div>
            </Col>
            <Col span={1}></Col>
          </Row>
        </div>
      </Spin>
      {showEquipmentModal && (
        <EquipmentModal
          show={showEquipmentModal}
          close={closeEquipmentModal}
          equipment={equipment}
        />
      )}
      {showTrayModal && (
        <TrayFormModal
          show={showTrayModal}
          close={() => setShowTrayModal(false)}
          tray={
            trayId
              ? tray
              : {
                  trayName: "New Tray",
                }
          }
          imageName={imageName}
        />
      )}
    </MainContainer>
  );
};

export default ManufacturerTrayFrom;
